import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  createDocumentTypeApi,
  deleteDocumentTypeApi,
  DOCUMENT_CATEGORY_LABELS,
  DocumentTypePayload,
  DpDocumentCategory,
  DpDocumentType,
  listDocumentTypesApi,
  updateDocumentTypeApi,
} from "../api/documentsApi";
import { seedProcessDefaultsApi } from "../api/processesApi";

const emptyForm = {
  code: "",
  name: "",
  category: "OUTRO" as DpDocumentCategory,
  requiresExpiry: false,
  defaultValidityDays: "",
  alertDaysBefore: "30",
  isRequiredOnAdmission: false,
  isActive: true,
};

/** Aba "Tipos de documento" em Cadastros. */
export default function DocumentTypesSettings({ token }: { token: string }) {
  const [types, setTypes] = useState<DpDocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Inclui inativos: esta é a tela de manutenção, onde reativar precisa ser possível.
      setTypes(await listDocumentTypesApi(token, true));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os tipos de documento.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (type?: DpDocumentType) => {
    setForm(
      type
        ? {
            code: type.code,
            name: type.name,
            category: type.category,
            requiresExpiry: type.requiresExpiry,
            defaultValidityDays: type.defaultValidityDays ? String(type.defaultValidityDays) : "",
            alertDaysBefore: String(type.alertDaysBefore),
            isRequiredOnAdmission: type.isRequiredOnAdmission,
            isActive: type.isActive,
          }
        : emptyForm
    );
    setModal({ id: type?.id });
  };

  const save = async () => {
    const code = form.code.trim().toUpperCase();
    if (!/^[A-Z0-9_]{2,40}$/.test(code)) {
      return toast.error("Código: 2 a 40 caracteres, só letras sem acento, números e _.");
    }
    if (form.name.trim().length < 2) return toast.error("Informe o nome.");

    const payload: DocumentTypePayload = {
      code,
      name: form.name.trim(),
      category: form.category,
      requiresExpiry: form.requiresExpiry,
      defaultValidityDays: form.defaultValidityDays ? Number(form.defaultValidityDays) : null,
      alertDaysBefore: Number(form.alertDaysBefore || 30),
      isRequiredOnAdmission: form.isRequiredOnAdmission,
      isActive: form.isActive,
    };

    setSaving(true);
    try {
      if (modal?.id) await updateDocumentTypeApi(token, modal.id, payload);
      else await createDocumentTypeApi(token, payload);
      toast.success("Tipo de documento salvo.");
      setModal(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type: DpDocumentType) => {
    if (!window.confirm(`Excluir o tipo "${type.name}"?`)) return;
    try {
      await deleteDocumentTypeApi(token, type.id);
      toast.success("Tipo excluído.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir.");
    }
  };

  const seed = async () => {
    setSaving(true);
    try {
      const { created } = await seedProcessDefaultsApi(token);
      toast.success(created.length ? `Criados: ${created.join(", ")}.` : "Os padrões já existiam — nada foi criado.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao criar os padrões.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dpControls">
        <button className="dpBtn dpBtn--primary" onClick={() => openModal()}>
          Novo tipo de documento
        </button>
        <button className="dpBtn dpBtn--secondary" onClick={seed} disabled={saving}>
          Criar padrões
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        "Criar padrões" monta os tipos básicos (contrato, ASO, termos, documentos pessoais) e os
        checklists padrão de admissão e desligamento, se ainda não existirem.
      </p>

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Categoria</th>
              <th>Validade</th>
              <th>Alerta</th>
              <th>Situação</th>
              <th style={{ width: 170 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="dpEmpty">
                  Carregando...
                </td>
              </tr>
            ) : types.length === 0 ? (
              <tr>
                <td colSpan={6} className="dpEmpty">
                  Nenhum tipo cadastrado.
                </td>
              </tr>
            ) : (
              types.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.name}</strong>
                    <div style={{ fontSize: 12, color: "var(--eco-muted)" }}>
                      {t.code}
                      {t.isRequiredOnAdmission ? " · exigido na admissão" : ""}
                    </div>
                  </td>
                  <td>{DOCUMENT_CATEGORY_LABELS[t.category]}</td>
                  <td>
                    {t.requiresExpiry
                      ? t.defaultValidityDays
                        ? `Obrigatória (padrão ${t.defaultValidityDays} dias)`
                        : "Obrigatória"
                      : t.defaultValidityDays
                        ? `Padrão ${t.defaultValidityDays} dias`
                        : "Não vence"}
                  </td>
                  <td>{t.alertDaysBefore} dias antes</td>
                  <td>
                    <span className={`dpBadge ${t.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                      {t.isActive ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td>
                    <div className="dpTable__actions">
                      <button
                        className="dpBtn dpBtn--secondary"
                        style={{ height: 30, padding: "0 10px" }}
                        onClick={() => openModal(t)}
                      >
                        Editar
                      </button>
                      <button
                        className="dpBtn dpBtn--danger"
                        style={{ height: 30, padding: "0 10px" }}
                        onClick={() => remove(t)}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.id ? "Editar tipo de documento" : "Novo tipo de documento"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)} disabled={saving}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <div className="dpFormGrid">
                <div className="dpFormGroup">
                  <label htmlFor="dtName">Nome *</label>
                  <input
                    id="dtName"
                    className="dpInput"
                    maxLength={120}
                    value={form.name}
                    disabled={saving}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="dtCode">Código *</label>
                  <input
                    id="dtCode"
                    className="dpInput"
                    maxLength={40}
                    placeholder="Ex.: NR35"
                    value={form.code}
                    disabled={saving}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="dtCategory">Categoria *</label>
                  <select
                    id="dtCategory"
                    className="dpSelect"
                    value={form.category}
                    disabled={saving}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as DpDocumentCategory }))}
                  >
                    {(Object.keys(DOCUMENT_CATEGORY_LABELS) as DpDocumentCategory[]).map((c) => (
                      <option key={c} value={c}>
                        {DOCUMENT_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="dtValidity">Validade padrão (dias)</label>
                  <input
                    id="dtValidity"
                    type="number"
                    min={1}
                    max={3650}
                    className="dpInput"
                    value={form.defaultValidityDays}
                    disabled={saving}
                    onChange={(e) => setForm((f) => ({ ...f, defaultValidityDays: e.target.value }))}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="dtAlert">Alertar com quantos dias de antecedência</label>
                  <input
                    id="dtAlert"
                    type="number"
                    min={0}
                    max={365}
                    className="dpInput"
                    value={form.alertDaysBefore}
                    disabled={saving}
                    onChange={(e) => setForm((f) => ({ ...f, alertDaysBefore: e.target.value }))}
                  />
                </div>
              </div>
              <label className="dpCheckbox">
                <input
                  type="checkbox"
                  checked={form.requiresExpiry}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, requiresExpiry: e.target.checked }))}
                />
                Todo documento deste tipo tem data de validade
              </label>
              <label className="dpCheckbox">
                <input
                  type="checkbox"
                  checked={form.isRequiredOnAdmission}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, isRequiredOnAdmission: e.target.checked }))}
                />
                Exigido na admissão
              </label>
              <label className="dpCheckbox">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Ativo
              </label>
            </div>
            <footer className="dpModal__footer">
              <button className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                Cancelar
              </button>
              <button className="dpBtn dpBtn--primary" onClick={save} disabled={saving}>
                Salvar
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
